import {Dropdown as PepsicoDsDropdown} from 'pepsico-ds'
import './_dropDown.scss'

/**
 * Dropdown component that renders a dropdown menu with various customization options.
 *
 * @param {Object} props - The properties object.
 * @param {any} [props.value] - The current value of the dropdown.
 * @param {Function} props.updateValue - Function to update the value of the dropdown.
 * @param {string} [props.label=''] - The label for the dropdown.
 * @param {Object} [props.schema] - The schema object containing options and other configurations.
 * @param {Object} [props.uiSchema] - The UI schema object containing UI-specific configurations.
 * @param {boolean} [props.required=false] - Indicates if the dropdown is required.
 * @param {boolean} [props.isError=false] - Indicates if there is an error.
 * @param {boolean} [props.isJsonWrap=true] - Determines if the dropdown should use JSON wrapping.
 * @param {Array} [props.childList=[]] - List of child elements for the dropdown.
 * @param {string} [props.dropdownHeader=''] - The header for the dropdown.
 * @param {string} [props.className=''] - Additional class names for the dropdown.
 * @param {boolean} [props.isDark=true] - Indicates if the dropdown should use a dark theme.
 * @param {('single'|'multiple')} [props.selection='single'] - The selection mode for the dropdown.
 * @param {('small'|'medium')} [props.size='medium'] - The size of the dropdown.
 * @param {string} [props.placeholder=''] - The placeholder text for the dropdown.
 * @param {string} [props.helperText=''] - The helper text for the dropdown.
 * @param {string} [props.errorMessage=''] - The error message for the dropdown.
 * @param {boolean} [props.disabled=false] - Indicates if the dropdown is disabled.
 * @param {boolean} [props.isClearable=true] - Indicates if the dropdown value can be cleared.
 * @param {boolean} [props.loading=false] - Indicates if the dropdown is in a loading state.
 * @param {string} [props.autocomplete=''] - The autocomplete attribute for the input.
 * @param {boolean} [props.hideMenuListOnSelection=true] - Determines if the menu list should be hidden after selection (applicable for multiple selection).
 * @returns {JSX.Element} The rendered Dropdown component.
 */
export default function Dropdown({
  value,
  updateValue,
  label = '',
  schema,
  uiSchema = {},
  required = false,
  isError = false,
  isJsonWrap = true,
  childList = [],
  dropdownHeader = '',
  className = '',
  isDark = true,
  selection = 'single',
  size = 'medium',
  placeholder = '',
  helperText = '',
  errorMessage = '',
  disabled = false,
  isClearable = true,
  loading = false,
  hideMenuListOnSelection = true,
  ...props
}) {
  const newValue = selectedValue => {
    updateValue(selectedValue)
  }

  const dropdownId = `dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`

  const getIsError = () => {
    if (uiSchema.value == '' || isError) {
      return true
    }
    return false
  }

  const getHelperText = () => {
    const isError = getIsError()
    if (isError) {
      return uiSchema?.errorMessage
    }
    return helperText
  }
  const selectionMap = {
    multiple: 'multipleTreeView',
  }

  const resolvedSelection = loading ? 'loading' : selectionMap[selection] || selection
  const resolvedSelectionForSchema = uiSchema?.selection
    ? selectionMap[uiSchema?.selection] || uiSchema?.selection
    : 'single'
  return (
    <>
      {isJsonWrap ? (
        <PepsicoDsDropdown
          id={dropdownId}
          isRequired={required}
          childList={schema.options}
          dropdownHeader={schema.header}
          style={{minWidth: '0px'}}
          className={uiSchema?.className || ''}
          isDark={uiSchema?.isDark || ''}
          label={label}
          error={getIsError()}
          helperText={getHelperText()}
          selection={resolvedSelectionForSchema}
          selectedValue={value}
          setSelectedValue={newValue}
          size={uiSchema.size || 'medium'}
          placeholder={uiSchema.placeholder || ''}
          disabled={disabled}
          isClearable={isClearable}
          hideMenuListOnSelection={hideMenuListOnSelection}
          {...props}
        />
      ) : (
        <PepsicoDsDropdown
          id={dropdownId}
          childList={childList}
          dropdownHeader={dropdownHeader}
          style={{minWidth: '0px'}}
          className={className || ''}
          isDark={isDark || ''}
          label={label}
          error={isError}
          selection={resolvedSelection}
          selectedValue={value}
          setSelectedValue={newValue}
          size={size}
          placeholder={placeholder}
          isRequired={required}
          helperText={isError ? errorMessage : helperText}
          disabled={disabled}
          isClearable={isClearable}
          hideMenuListOnSelection={hideMenuListOnSelection}
          {...props}
        />
      )}
    </>
  )
}
